import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { keepPreviousData, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { ApiError, api, request, uploadFile, type Attachment, type MailRecipientOption } from './api';
import type { MentionType, Placed } from './mentions';

/**
 * Chat (operator, 2026-10-19): two employees' conversation, on a page like
 * WhatsApp Web under Mail — inside the system only, as mail is. Groups too
 * (operator, 2026-10-22): anyone starts one and invites people, and nobody is
 * in a group until they accept.
 *
 * The page keeps each open chat's messages in the query cache and adds to it:
 * the newest page when a chat opens, older pages as it scrolls up, and the
 * messages after the newest it has whenever `GET /chat/events` — a long poll —
 * says the chat changed. What is being sent waits in the chat's outbox, shown
 * at once, until the server's copy takes its place.
 */

export type ChatKind = 'DIRECT' | 'GROUP';

/** A group's own line between its messages: started, renamed, someone joined, left or was removed. */
export type ChatEvent = 'GROUP_CREATED' | 'GROUP_RENAMED' | 'MEMBER_JOINED' | 'MEMBER_LEFT' | 'MEMBER_REMOVED';

/** A name, as a message carries its writer's and a group's line whom it names. */
export interface ChatName {
  id: string;
  fullName: string;
  fullNameAr: string | null;
}

export interface ChatPerson {
  id: string;
  fullName: string;
  fullNameAr: string | null;
  jobTitle: string | null;
  /** Not active: no longer an employee — the chat reads, but nothing more can be sent. */
  isActive: boolean;
  /** Has the system open right now: a request of theirs within the last minute and a half. */
  online?: boolean;
}

/** How far each of the two has read: the caller, and the other person — which shows the caller's messages read. */
export interface ChatReads {
  mine: number;
  theirs: number;
}

/** A chat's newest message, as its row in the list shows it. */
export interface ChatPreview {
  id: string;
  seq: number;
  mine: boolean;
  body: string | null;
  pictures: number;
  voiceNotes: number;
  videos: number;
  files: number;
  /** A sticker — its picture counted in `pictures` too. Missing from an API of before stickers. */
  sticker?: boolean;
  /** Who wrote it — a group's list names them. */
  sender?: ChatName;
  /** A group's own line, not a message: `body` is the group's name when it was started or renamed. */
  event?: ChatEvent | null;
  /** Whom the line names: who was removed, or who invited the one who joined. */
  eventUser?: ChatName | null;
  createdAt: string;
}

/** A group, as its row in the list says it. */
export interface ChatGroupSummary {
  name: string;
  /** How many are in it. */
  members: number;
  /** The caller looks after it: renames it, takes people out, makes others admins. */
  admin: boolean;
  createdById: string | null;
}

/** A row of `GET /chat/conversations`, and `GET /chat/conversations/:id`. */
export interface ChatSummary {
  id: string;
  /** Missing from an API of before groups, where every chat is two people's. */
  kind?: ChatKind;
  /** Two people's chat: the other person. Null for a group. */
  other: ChatPerson | null;
  /** A group: its name, how many are in it. Null for two people's chat. */
  group?: ChatGroupSummary | null;
  lastMessage: ChatPreview | null;
  lastMessageAt: string | null;
  /** The others' messages the caller has not read. */
  unread: number;
  /** Of those, how many mention the caller — the list's "@". Missing from an API of before mentions. */
  unreadMentions?: number;
  reads: ChatReads;
  createdAt: string;
}

/** A group's member, as its details list them. */
export interface ChatGroupMember extends ChatPerson {
  /** Looks after the group. */
  admin: boolean;
  /** The caller. */
  me: boolean;
  joinedAt: string;
}

/** Someone invited to a group, not in it until they accept. */
export interface ChatGroupInvitee {
  id: string;
  user: ChatPerson;
  invitedBy: ChatName;
  createdAt: string;
  /** Whoever sent it, or an admin, takes it back. */
  canCancel: boolean;
}

/** `GET /chat/conversations/:id`: a group comes with its members and the invitations still waiting. */
export interface ChatDetail extends ChatSummary {
  members?: ChatGroupMember[];
  invitations?: ChatGroupInvitee[];
}

/** An invitation of the caller's, waiting for their answer (`GET /chat/invitations`). */
export interface ChatInvitation {
  id: string;
  createdAt: string;
  invitedBy: ChatName;
  /** The group: its name, how many are in it, and the first few of them. */
  group: { id: string; name: string; members: number; people: ChatName[] };
}

export const isGroupChat = (chat: Pick<ChatSummary, 'kind'> | null | undefined) => chat?.kind === 'GROUP';

export interface ChatList {
  /** Where the server's change feed stood before it read the list: the first poll starts from it. */
  cursor: string;
  items: ChatSummary[];
}

/** The message a reply answers, as quoted above it. */
export interface ChatQuote {
  id: string;
  seq: number;
  senderId: string;
  /** Who wrote it — a group's quote names them. */
  sender?: ChatName;
  mine: boolean;
  body: string | null;
  pictures: number;
  voiceNotes: number;
  videos: number;
  files: number;
  /** A sticker — `picture` is it. */
  sticker?: boolean;
  /** Its first picture, shown small beside the quote. */
  picture: { url: string } | null;
  /** Said in a group before the caller was invited: who wrote it, nothing of what. */
  hidden?: boolean;
}

/**
 * A sticker, as a message carries it and the tray offers it (operator,
 * 2026-10-20: "in chat system i want to add stickers to chats"). The packs are
 * the administrators' (`chat.stickers.manage`); everyone sends them.
 */
export interface ChatSticker {
  id: string;
  /** What it is called — from its file's name: the tooltip, and what a screen reader says. */
  label: string;
  /** Its picture, through a signed link. */
  url: string;
}

/** A pack the chat offers, with its stickers in order. */
export interface ChatStickerPack {
  id: string;
  name: string;
  nameAr: string | null;
  stickers: ChatSticker[];
}

/** An emoji someone in the chat put on a message — one each (operator, 2026-10-20). */
export interface ChatReaction {
  emoji: string;
  userId: string;
  /** Whose it is — a group's reactions name them. Missing from an API of before groups. */
  fullName?: string;
  fullNameAr?: string | null;
  /** The caller's own. */
  mine: boolean;
  createdAt: string;
}

/** The reactions offered at once, as WhatsApp offers them; any other emoji is one press further. */
export const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'] as const;

/**
 * @someone, #a task or #a ticket in a message's words (operator, 2026-10-22):
 * where it is — offset and length in UTF-16 units, as a JavaScript string
 * counts — and what it names. Someone comes with their name; a task or a ticket
 * with `canOpen`, and only then with what it is now.
 */
export interface ChatMention extends Placed {
  /** Someone: their name, whether they are still an employee, whether it is the reader. */
  fullName?: string;
  fullNameAr?: string | null;
  isActive?: boolean;
  me?: boolean;
  /** A task or a ticket: whether the reader may open it — one they may not is the words alone. */
  canOpen?: boolean;
  /** A task's title, or a ticket's subject. */
  title?: string;
  status?: string;
  /** A ticket's number. */
  number?: string;
  /** A Leaders Task: its page is under Leaders Tasks. */
  leader?: boolean;
}

/** A task or a ticket the "#" list offers: `label` is how the message names it. */
export interface ChatReference {
  type: Exclude<MentionType, 'USER'>;
  id: string;
  label: string;
  title: string;
  status: string;
  number?: string;
  leader?: boolean;
}

/** Where a task or ticket a message mentions opens. */
export function referencePath(m: Pick<ChatMention, 'type' | 'id' | 'leader'>): string {
  if (m.type === 'TICKET') return `/tickets/${m.id}`;
  return m.leader ? `/leader-tasks/${m.id}` : `/tasks/${m.id}`;
}

/**
 * The "#" list: the tickets and tasks the caller can open whose number, subject
 * or title has `q` — asked as it is typed, the last answer kept on screen until
 * the next comes.
 */
export function useReferences(q: string, enabled: boolean) {
  return useQuery({
    queryKey: ['chat', 'references', q],
    queryFn: ({ signal }) => request<{ items: ChatReference[] }>('/chat/references', { params: { q }, signal }),
    enabled,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export interface ChatMessage {
  id: string;
  /** The order messages were written in. */
  seq: number;
  conversationId: string;
  senderId: string;
  /** Who wrote it — a group shows the name over it. */
  sender?: ChatName;
  /** A group's own line, not a message: the sender did it, `eventUser` is whom it names. */
  event?: ChatEvent | null;
  eventUser?: ChatName | null;
  mine: boolean;
  body: string | null;
  /** The sender's own id for it — only on the caller's own messages. */
  clientId: string | null;
  /** Passed on from another chat. */
  forwarded: boolean;
  /** The message of this chat it answers. */
  replyTo: ChatQuote | null;
  /** A sticker, sent alone. Its picture is in `attachments` too, for an app of before stickers. */
  sticker?: ChatSticker | null;
  createdAt: string;
  attachments: Attachment[];
  /** The first put on first. Missing from an API of before reactions. */
  reactions?: ChatReaction[];
  /** @someone, #a task, #a ticket in `body`, in order. Missing from an API of before mentions. */
  mentions?: ChatMention[];
}

interface ChatPage {
  items: ChatMessage[];
  hasMore: boolean;
  gap: boolean;
  reads: ChatReads;
  /** The chat's reaction changes so far: what the next catch-up asks after. */
  reactionSeq?: number;
  /** Catching up: the messages up to `after` whose reactions changed since `reactedAfter`. */
  reacted?: Array<{ id: string; reactions: ChatReaction[] }>;
}

interface ChatChanges {
  cursor: string;
  conversationIds: string[];
  reset: boolean;
}

/** A message on its way: shown at once, until the server's copy takes its place. */
export interface Outgoing {
  clientId: string;
  body: string | null;
  createdAt: string;
  status: 'sending' | 'failed';
  error?: string;
  /** Files already uploaded, sent with it by id. */
  fileIds?: string[];
  attachments?: Attachment[];
  /** A voice note just recorded: uploaded as it is sent. */
  voice?: { blob: Blob; url: string; durationMs: number; name: string };
  /** The message it answers, and its quote while it is on its way. */
  replyToId?: string;
  quote?: ChatQuote;
  /** A sticker, sent alone. */
  sticker?: ChatSticker;
  /** The people, tasks and tickets its words mention. */
  mentions?: ChatMention[];
}

/** One chat's messages as the page holds them, oldest first, and what is still being sent. */
export interface ThreadState {
  items: ChatMessage[];
  /** Older messages are left to load. */
  hasMore: boolean;
  reads: ChatReads;
  outbox: Outgoing[];
  /** The chat's reaction changes this page has caught up with. */
  reactionSeq: number;
}

export type ChatPersonOption = MailRecipientOption & { online?: boolean };

/** A forward goes to this many people at most, as WhatsApp allows — the API's own limit. */
export const MAX_FORWARD_TO = 5;

const isPicture = (mime: string) => /^image\/(png|jpeg|gif|webp)$/.test(mime);

/** A message as its reply quotes it, made here while the reply is on its way — the server sends its own after. */
export function quoteOf(message: ChatMessage): ChatQuote {
  const files = message.attachments.map((a) => a.file);
  const voice = files.filter((f) => f.kind === 'VOICE_NOTE').length;
  const pictures = files.filter((f) => f.kind !== 'VOICE_NOTE' && isPicture(f.mimeType));
  const videos = files.filter((f) => f.kind !== 'VOICE_NOTE' && f.mimeType.startsWith('video/')).length;
  return {
    id: message.id,
    seq: message.seq,
    senderId: message.senderId,
    sender: message.sender,
    mine: message.mine,
    body: message.body,
    pictures: pictures.length,
    voiceNotes: voice,
    videos,
    files: files.length - voice - pictures.length - videos,
    sticker: !!message.sticker,
    picture: message.sticker ? { url: message.sticker.url } : pictures[0] ? { url: pictures[0].url } : null,
  };
}

export const chatKeys = {
  all: ['chat'] as const,
  list: ['chat', 'list'] as const,
  unread: ['chat', 'unread'] as const,
  people: ['chat', 'people'] as const,
  stickers: ['chat', 'stickers'] as const,
  invitations: ['chat', 'invitations'] as const,
  one: (id: string) => ['chat', 'one', id] as const,
  thread: (id: string) => ['chat', 'messages', id] as const,
};

/**
 * The caller's group invitations waiting for an answer — on top of the chats,
 * with Accept and Decline. The long poll says when one comes or goes; the half
 * minute is for a page that missed it.
 */
export function useInvitations() {
  return useQuery({
    queryKey: chatKeys.invitations,
    queryFn: () => api.get<ChatInvitation[]>('/chat/invitations'),
    refetchInterval: 30_000,
  });
}

/** What a group's change moves: the chats, the badge, the invitations, and the group itself. */
export function refreshGroup(qc: QueryClient, id?: string): void {
  void qc.invalidateQueries({ queryKey: chatKeys.list });
  void qc.invalidateQueries({ queryKey: chatKeys.unread });
  void qc.invalidateQueries({ queryKey: chatKeys.invitations });
  if (id) {
    void qc.invalidateQueries({ queryKey: chatKeys.one(id) });
    void catchUp(qc, id);
  }
}

export function newClientId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-8xxx-xxxxxxxxxxxx'.replace(/x/g, () => Math.floor(Math.random() * 16).toString(16));
}

/** This page's own id for the server, so two tabs are told apart. */
const TAB = newClientId();

/** What is typed but not sent yet, per person and chat (`<userId>:<chatId>`), while the page is open — as WhatsApp keeps it. */
export const drafts = new Map<string, string>();

/** And the mentions in it, with it. */
export const draftMentions = new Map<string, ChatMention[]>();

// ---------------------------------------------------------------------------
// The cache
// ---------------------------------------------------------------------------

function merge(items: ChatMessage[], incoming: ChatMessage[]): ChatMessage[] {
  if (incoming.length === 0) return items;
  const byId = new Map(items.map((m) => [m.id, m]));
  for (const m of incoming) byId.set(m.id, m);
  return [...byId.values()].sort((a, b) => a.seq - b.seq);
}

/** What the server now has is no longer on its way. */
function settle(outbox: Outgoing[], incoming: ChatMessage[]): Outgoing[] {
  const sent = new Set(incoming.map((m) => m.clientId).filter((x): x is string => !!x));
  if (sent.size === 0) return outbox;
  for (const item of outbox) {
    // Its local copy, once nothing plays it any more.
    if (item.voice && sent.has(item.clientId)) setTimeout(() => URL.revokeObjectURL(item.voice!.url), 60_000);
  }
  return outbox.filter((o) => !sent.has(o.clientId));
}

/** The reactions that changed, on the messages the page holds — a message it does not hold loads with its own. */
function react(items: ChatMessage[], reacted: ChatPage['reacted']): ChatMessage[] {
  if (!reacted?.length) return items;
  const changed = new Map(reacted.map((r) => [r.id, r.reactions]));
  return items.map((m) => (changed.has(m.id) ? { ...m, reactions: changed.get(m.id) } : m));
}

/** Read marks only move forward, whichever answer arrives first. */
const later = (a: ChatReads, b: ChatReads): ChatReads => ({
  mine: Math.max(a.mine, b.mine),
  theirs: Math.max(a.theirs, b.theirs),
});

/**
 * The newest page of a chat. Anything newer the page already holds — a message
 * sent while this was on its way — is kept, and so is the outbox.
 */
export async function loadThread(qc: QueryClient, id: string): Promise<ThreadState> {
  const page = await api.get<ChatPage>(`/chat/conversations/${id}/messages`);
  const prev = qc.getQueryData<ThreadState>(chatKeys.thread(id));
  const newest = page.items.at(-1)?.seq ?? 0;
  const newer = prev?.items.filter((m) => m.seq > newest) ?? [];
  const items = merge(page.items, newer);
  return {
    items,
    hasMore: page.hasMore,
    reads: prev ? later(prev.reads, page.reads) : page.reads,
    outbox: settle(prev?.outbox ?? [], items),
    reactionSeq: page.reactionSeq ?? 0,
  };
}

const catching = new Map<string, { again: boolean; running: Promise<void> }>();

/**
 * Whatever a chat the page holds has gained since its newest message, the
 * reactions changed since it last asked, and how far each side has read. Asked
 * again while it runs, it runs once more.
 */
export function catchUp(qc: QueryClient, id: string): Promise<void> {
  const running = catching.get(id);
  if (running) {
    running.again = true;
    return running.running;
  }
  const entry = { again: false, running: Promise.resolve() };
  entry.running = (async () => {
    do {
      entry.again = false;
      const state = qc.getQueryData<ThreadState>(chatKeys.thread(id));
      // Not open here: it loads afresh when it is.
      if (!state) return;
      const after = state.items.at(-1)?.seq ?? 0;
      const page = await api.get<ChatPage>(`/chat/conversations/${id}/messages`, {
        after,
        reactedAfter: state.reactionSeq,
      });
      if (page.gap) {
        await qc.invalidateQueries({ queryKey: chatKeys.thread(id) });
        return;
      }
      qc.setQueryData<ThreadState>(chatKeys.thread(id), (s) =>
        s && {
          ...s,
          items: merge(react(s.items, page.reacted), page.items),
          reads: later(s.reads, page.reads),
          outbox: settle(s.outbox, page.items),
          reactionSeq: page.reactionSeq ?? s.reactionSeq,
        },
      );
    } while (entry.again);
  })()
    .catch(() => undefined)
    .finally(() => catching.delete(id));
  catching.set(id, entry);
  return entry.running;
}

/** The page of messages before the oldest one held. */
export async function loadOlder(qc: QueryClient, id: string): Promise<void> {
  const state = qc.getQueryData<ThreadState>(chatKeys.thread(id));
  const oldest = state?.items[0]?.seq;
  if (!state?.hasMore || oldest === undefined) return;
  const page = await api.get<ChatPage>(`/chat/conversations/${id}/messages`, { before: oldest });
  qc.setQueryData<ThreadState>(chatKeys.thread(id), (s) => s && { ...s, items: merge(page.items, s.items), hasMore: page.hasMore });
}

// ---------------------------------------------------------------------------
// Sending and reading
// ---------------------------------------------------------------------------

/** Puts a message in the chat's outbox, where it shows at once, and sends it. */
export function enqueue(qc: QueryClient, id: string, item: Outgoing): void {
  qc.setQueryData<ThreadState>(chatKeys.thread(id), (s) => s && { ...s, outbox: [...s.outbox, item] });
  void deliver(qc, id, item);
}

/**
 * Sends one message of the outbox — a voice note uploads first. Sent again
 * after a failure, it keeps its id, so the server keeps one message.
 */
export async function deliver(qc: QueryClient, id: string, item: Outgoing): Promise<void> {
  const key = chatKeys.thread(id);
  const patch = (change: Partial<Outgoing>) =>
    qc.setQueryData<ThreadState>(key, (s) =>
      s && { ...s, outbox: s.outbox.map((o) => (o.clientId === item.clientId ? { ...o, ...change } : o)) },
    );
  patch({ status: 'sending', error: undefined });
  try {
    let fileIds = item.fileIds ?? [];
    if (item.voice && fileIds.length === 0) {
      const file = await uploadFile(item.voice.blob, item.voice.name, {
        kind: 'VOICE_NOTE',
        durationMs: item.voice.durationMs,
      });
      fileIds = [file.id];
      // A retry sends the same file rather than uploading it again.
      patch({ fileIds });
    }
    const message = await api.post<ChatMessage>(`/chat/conversations/${id}/messages`, {
      body: item.body ?? undefined,
      fileIds: fileIds.length ? fileIds : undefined,
      clientId: item.clientId,
      replyToId: item.replyToId,
      stickerId: item.sticker?.id,
      mentions: item.mentions?.length
        ? item.mentions.map(({ type, id: named, offset, length }) => ({ type, id: named, offset, length }))
        : undefined,
    });
    qc.setQueryData<ThreadState>(key, (s) =>
      s && {
        ...s,
        items: merge(s.items, [message]),
        outbox: settle(s.outbox, [message]),
        reads: { ...s.reads, mine: Math.max(s.reads.mine, message.seq) },
      },
    );
    void qc.invalidateQueries({ queryKey: chatKeys.list });
  } catch (e) {
    // A sticker taken out of the packs meanwhile: the tray is read again, without it.
    if (item.sticker && e instanceof ApiError && (e.details as { reason?: string } | undefined)?.reason === 'STICKER_GONE') {
      void qc.invalidateQueries({ queryKey: chatKeys.stickers });
    }
    // Someone who left, or not in the group (2026-10-22), or a task or ticket out of reach, named in it: Try again
    // sends its words without that link.
    const details = e instanceof ApiError ? (e.details as { reason?: string; mentions?: Array<{ type: string; id: string }> }) : undefined;
    if ((details?.reason === 'MENTION_GONE' || details?.reason === 'NOT_IN_GROUP') && item.mentions) {
      const gone = details.mentions ?? [];
      patch({ mentions: item.mentions.filter((m) => !gone.some((g) => g.type === m.type && g.id === m.id)) });
    }
    patch({ status: 'failed', error: e instanceof Error ? e.message : String(e) });
  }
}

/** Takes a message that could not be sent out of the outbox. */
export function discard(qc: QueryClient, id: string, clientId: string): void {
  qc.setQueryData<ThreadState>(chatKeys.thread(id), (s) => {
    if (!s) return s;
    const item = s.outbox.find((o) => o.clientId === clientId);
    if (item?.voice) URL.revokeObjectURL(item.voice.url);
    return { ...s, outbox: s.outbox.filter((o) => o.clientId !== clientId) };
  });
}

/**
 * Reads a chat up to a message: shown read here at once — in the list too —
 * then told to the server, which shows it read to the other person.
 */
export async function markRead(qc: QueryClient, id: string, seq: number): Promise<void> {
  qc.setQueryData<ThreadState>(chatKeys.thread(id), (s) => s && { ...s, reads: { ...s.reads, mine: Math.max(s.reads.mine, seq) } });
  qc.setQueryData<ChatList>(chatKeys.list, (list) =>
    list && {
      ...list,
      items: list.items.map((c) =>
        c.id === id && (c.lastMessage?.seq ?? 0) <= seq
          ? { ...c, unread: 0, unreadMentions: 0, reads: { ...c.reads, mine: Math.max(c.reads.mine, seq) } }
          : c,
      ),
    },
  );
  try {
    await api.post(`/chat/conversations/${id}/read`, { seq });
  } finally {
    void qc.invalidateQueries({ queryKey: chatKeys.unread });
    // Its notice in the bell is read with it.
    void qc.invalidateQueries({ queryKey: ['notifications'] });
  }
}

/**
 * The caller's reaction on a message — an emoji, or null to take theirs off.
 * Shown at once; the server's answer then says how the message's reactions
 * stand, and a refusal puts them back as they were.
 */
export async function reactTo(
  qc: QueryClient,
  id: string,
  messageId: string,
  emoji: string | null,
  me: string,
): Promise<void> {
  const key = chatKeys.thread(id);
  const set = (reactions: (current: ChatReaction[]) => ChatReaction[]) =>
    qc.setQueryData<ThreadState>(key, (s) =>
      s && { ...s, items: s.items.map((m) => (m.id === messageId ? { ...m, reactions: reactions(m.reactions ?? []) } : m)) },
    );
  const before = qc.getQueryData<ThreadState>(key)?.items.find((m) => m.id === messageId)?.reactions ?? [];
  set((current) => [
    ...current.filter((r) => !r.mine),
    ...(emoji ? [{ emoji, userId: me, mine: true, createdAt: new Date().toISOString() }] : []),
  ]);
  try {
    const message = emoji
      ? await api.put<ChatMessage>(`/chat/messages/${messageId}/reaction`, { emoji })
      : await api.del<ChatMessage>(`/chat/messages/${messageId}/reaction`);
    set(() => message.reactions ?? []);
  } catch (e) {
    set(() => before);
    throw e;
  }
}

/** Forward: the message, from the caller, into their chat with each of these people, and into each of these groups. */
export async function forwardMessage(
  qc: QueryClient,
  messageId: string,
  userIds: string[],
  groupIds: string[] = [],
): Promise<string[]> {
  const { conversationIds } = await api.post<{ conversationIds: string[] }>(`/chat/messages/${messageId}/forward`, {
    userIds,
    ...(groupIds.length ? { groupIds } : {}),
  });
  void qc.invalidateQueries({ queryKey: chatKeys.list });
  return conversationIds;
}

// ---------------------------------------------------------------------------
// Stickers
// ---------------------------------------------------------------------------

/**
 * The packs the chat offers, in the administrators' order. They change seldom:
 * read again after a few minutes, or when a send says a sticker is gone. The
 * links last twelve hours.
 */
export function useStickerTray() {
  return useQuery({
    queryKey: chatKeys.stickers,
    queryFn: () => api.get<{ packs: ChatStickerPack[] }>('/chat/stickers'),
    staleTime: 5 * 60_000,
  });
}

const RECENT_STICKERS_KEY = 'viganium.chat.recentStickers';
const RECENT_STICKERS_MAX = 16;

/** The stickers sent last from this browser, newest first — their ids, as the emoji's are kept. */
export function recentStickers(): string[] {
  try {
    const saved = JSON.parse(localStorage.getItem(RECENT_STICKERS_KEY) ?? '[]');
    return Array.isArray(saved) ? saved.filter((id): id is string => typeof id === 'string').slice(0, RECENT_STICKERS_MAX) : [];
  } catch {
    return [];
  }
}

export function rememberSticker(id: string): void {
  try {
    const next = [id, ...recentStickers().filter((x) => x !== id)].slice(0, RECENT_STICKERS_MAX);
    localStorage.setItem(RECENT_STICKERS_KEY, JSON.stringify(next));
  } catch {
    /* a private window may keep nothing */
  }
}

// ---------------------------------------------------------------------------
// Enter sends — or starts a new line
// ---------------------------------------------------------------------------

const ENTER_KEY = 'viganium.chat.enterSends'; // 'off' = Enter starts a new line
const enterListeners = new Set<() => void>();
/** The choice made on this page, for a browser that keeps nothing (a private window). */
let enterChoice: boolean | null = null;

function readEnterSends(): boolean {
  try {
    const saved = localStorage.getItem(ENTER_KEY);
    if (saved !== null) return saved !== 'off';
  } catch {
    /* nothing kept: the page's own choice, or the default */
  }
  return enterChoice ?? true;
}

function subscribeEnterSends(listener: () => void): () => void {
  enterListeners.add(listener);
  const onStorage = (e: StorageEvent) => {
    if (e.key === ENTER_KEY) listener();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    enterListeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

/**
 * Whether Enter sends a message (Shift+Enter starting a new line), or starts a
 * new line (Ctrl+Enter sending) — each person's choice, kept per browser like
 * the theme, as WhatsApp's "Enter is send". Sends by default.
 */
export function useEnterSends(): [boolean, (on: boolean) => void] {
  const on = useSyncExternalStore(subscribeEnterSends, readEnterSends, () => true);
  const set = useCallback((next: boolean) => {
    enterChoice = next;
    try {
      localStorage.setItem(ENTER_KEY, next ? 'on' : 'off');
    } catch {
      /* a private window may keep nothing; the choice lasts this page */
    }
    enterListeners.forEach((listener) => listener());
  }, []);
  return [on, set];
}

// ---------------------------------------------------------------------------
// The long poll
// ---------------------------------------------------------------------------

const sleep = (ms: number, signal: AbortSignal) =>
  new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener('abort', () => {
      clearTimeout(timer);
      resolve();
    });
  });

const lookedAt = () => document.visibilityState === 'visible' && document.hasFocus();

/**
 * Whether the person is looking at the page: its tab in view and its window the
 * one in use — as WhatsApp Web counts it. A window left open beside another
 * program reads nothing by itself, and its messages ring the bell.
 */
export function usePageInView(): boolean {
  const [inView, setInView] = useState(lookedAt);
  useEffect(() => {
    const update = () => setInView(lookedAt());
    document.addEventListener('visibilitychange', update);
    window.addEventListener('focus', update);
    window.addEventListener('blur', update);
    return () => {
      document.removeEventListener('visibilitychange', update);
      window.removeEventListener('focus', update);
      window.removeEventListener('blur', update);
    };
  }, []);
  return inView;
}

/**
 * While the chat page is open: asks the server, again and again, what changed —
 * each question held until something does — and fetches it. `viewing` is the
 * chat on screen, while the person looks at the page: its messages ring no
 * bell, since the page reads them as they arrive. A new `viewing` asks afresh
 * at once.
 */
export function useChatEvents(viewing: string | null): void {
  const qc = useQueryClient();
  const looking = usePageInView();
  const cursor = useRef<string | null>(null);
  const inView = looking ? viewing : null;

  useEffect(() => {
    const controller = new AbortController();
    void (async () => {
      let failures = 0;
      while (!controller.signal.aborted) {
        try {
          const since = cursor.current ?? qc.getQueryData<ChatList>(chatKeys.list)?.cursor;
          const changes = await request<ChatChanges>('/chat/events', {
            params: { since, viewing: inView, tab: TAB },
            signal: controller.signal,
          });
          failures = 0;
          cursor.current = changes.cursor;
          apply(qc, changes);
        } catch {
          if (controller.signal.aborted) return;
          // The network, a restart, the rate limit: wait a little longer each time, up to half a minute.
          failures++;
          await sleep(Math.min(30_000, 1000 * 2 ** Math.min(failures, 5)), controller.signal);
        }
      }
    })();
    return () => controller.abort();
  }, [qc, inView]);
}

function apply(qc: QueryClient, changes: ChatChanges): void {
  if (changes.reset) {
    void qc.invalidateQueries({ queryKey: chatKeys.all });
    return;
  }
  if (changes.conversationIds.length === 0) return;
  void qc.invalidateQueries({ queryKey: chatKeys.list });
  void qc.invalidateQueries({ queryKey: chatKeys.unread });
  // A group's invitation came, or went: the chats' top.
  void qc.invalidateQueries({ queryKey: chatKeys.invitations });
  for (const id of changes.conversationIds) {
    void catchUp(qc, id);
    void qc.invalidateQueries({ queryKey: chatKeys.one(id) });
  }
  // A message in another chat rings the bell now, not at its next poll.
  void qc.invalidateQueries({ queryKey: ['notifications', 'count'] });
}
